import type { ResultOf } from '@graphql-typed-document-node/core'
import { ChevronBottomOutlineIcon } from '@ikyucom/design-system-icons-react'
import { min, uniq } from 'lodash-es'
import { type FC, type PropsWithChildren, useCallback, useId, useState } from 'react'
import type { ChangeEvent, MouseEvent } from 'react'
import { useCourseMenuNavigation, usePlanItemNavigation } from '~/features/guide/courses/hooks'
import {
  PlanDiscountLabel,
  PlanFreeFlowLabel,
  PlanPrice,
  PlanRequestReservationLabel,
  PlanSubLabels,
} from '~/features/plan'
import { money } from '~/lib/money/money'
import { planDetailPagePath } from '~/lib/path/path'
import { planNameInCourse } from '~/lib/plan/name'
import { useIsPC } from '~/lib/ua'
import { useBack, useReserveOverlay, useSendClickEvent } from '~/navigation'
import { type RecipeVariantProps, css, cva, cx, sva } from '~/styled-system/css'
import { hstack } from '~/styled-system/patterns'
import { Button } from '~/ui'
import { Collapsible } from '~/ui/Collapsible'
import Image from '../../image/Image'
import { StackableSlideUpModal } from '../../modal/StackableSlideUpModal'
import { PointText } from '../../plan/PlanDetail'
import type { Plan } from '../../plan/PlanDetail'
import CourseMenu from '../CourseMenu'
import { CourseNoImage } from '../CourseNoImage'
import type { CourseSelectionInfo } from '../CourseSelectionInfo.frag'
import type { CourseItemProps, PlanItemProps } from '../type'
import { RESERVE_LABEL } from './label'

const courseItemStyles = sva({
  slots: ['header', 'details', 'plans', 'label', 'labelText'],
  base: {
    header: {
      color: 'link',
      fontSize: 'sm',
      width: 'full',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      columnGap: '1',
    },
    details: {
      width: 'full',
    },
    plans: {
      divideY: 'thin',
      divideColor: 'gray.200',
    },
  },
})

export function CourseItem({
  course,
  plans,
  restaurant,
  pageType,
  defaultOpen = false,
}: CourseItemProps) {
  const onClickMenu = useCourseMenuNavigation(course, plans, restaurant)
  const sendClickEvent = useSendClickEvent()
  const [open, setOpen] = useState(defaultOpen)
  const onOpenChange = useCallback(
    (open: boolean) => {
      // アコーディオンが開いた時だけ ClickEvent を送信する
      if (open) {
        sendClickEvent({
          courseList: 'course',
          click_event: `newReservationFlow/${pageType}/course`,
        })
        setOpen(true)
      } else {
        setOpen(false)
      }
    },
    [pageType, sendClickEvent]
  )
  const classes = courseItemStyles()
  return (
    <div
      className={css({
        border: 'medium',
        rounded: 'md',
        bg: 'white',
        w: 'full',
        overflow: 'hidden',
      })}
    >
      <Header course={course} plans={plans} onClickMenu={onClickMenu} />
      <Collapsible
        header={
          // アコーディオンが開いたらテキストを変更する
          <>
            {!open ? (
              <p className={css({ display: 'flex', alignItems: 'center', columnGap: '1' })}>
                <span>このコースのプランをみる</span>
                <span>({plans.length})</span>
              </p>
            ) : (
              <>
                <span>閉じる</span>
              </>
            )}
          </>
        }
        className={classes.details}
        onOpenChange={onOpenChange}
        defaultOpen={defaultOpen}
        headerPosition="center"
        color="neutral"
        icon="course"
        size="course"
        border="top"
      >
        <div className={classes.plans}>
          {plans.map((plan) => (
            <PlanItem key={plan.planId} plan={plan} course={course} restaurant={restaurant} />
          ))}
        </div>
      </Collapsible>
    </div>
  )
}

type HeaderProps = {
  course: ResultOf<typeof CourseSelectionInfo>
  plans: Plan[]
  onClickMenu: (e: MouseEvent<HTMLButtonElement>) => void
}

const headerStyles = sva({
  slots: ['root', 'container', 'content', 'pricing', 'price'],
  base: {
    root: {
      width: 'full',
      display: 'flex',
      alignItems: 'start',
      justifyContent: 'start',
      flex: '1',
      p: '3',
      pb: '2',
      columnGap: '2',
    },
    container: {
      display: 'flex',
      flex: '1',
      flexDirection: 'column',
      justifyContent: 'between',
    },
    content: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      spaceX: '3',
    },
    pricing: {
      width: 'full',
      display: 'flex',
      alignItems: 'end',
      justifyContent: 'end',
      marginTop: 'auto',
    },
    price: {
      flexGrow: 1,
      textAlign: 'right',
      fontSize: 'lg',
      fontWeight: 'bold',
    },
  },
})

function Header({ course, plans, onClickMenu }: HeaderProps) {
  const imageWidth = 96
  const imageHeight = 68

  const priceList = plans.map((p) => p.bestPrice)
  const bestPrice = min(priceList)
  const priceVaried = uniq(priceList).length > 1 || plans.some((p) => p.priceVaried)
  const classes = headerStyles()

  return (
    <button className={classes.root} type="button" onClick={onClickMenu}>
      {course.images.length !== 0 ? (
        <button type="button" onClick={onClickMenu}>
          <Image
            alt={`${course.name} - ${course.restaurant.name}`}
            src={course.images[0].url}
            width={imageWidth}
            height={imageHeight}
            className={css({ rounded: 'default' })}
          />
        </button>
      ) : (
        <CourseNoImage width={imageWidth} height={imageHeight} />
      )}
      <div className={classes.container} style={{ minHeight: imageHeight }}>
        <p
          className={css({
            textAlign: 'left',
            fontWeight: 'bold',
            color: 'link',
            lineHeight: 'normal',
            fontSize: 'md',
          })}
        >
          {course.name}
        </p>
        {bestPrice !== undefined && bestPrice !== 0 && (
          <div className={classes.pricing}>
            <p className={classes.price}>
              {money(bestPrice)}
              <span className={css({ fontSize: 'sm' })}>円{priceVaried && <>〜</>}</span>
            </p>
          </div>
        )}
      </div>
    </button>
  )
}

export function CourseMenuModal() {
  const back = useBack()
  const onClick = useCallback(() => {
    // back を直接 onClick に渡してしまうと event が渡されるので一段階挟む
    back()
  }, [back])
  const [context, willBeUnmounted] = useReserveOverlay('COURSE_MODAL')
  const isPC = useIsPC()
  if (context === undefined) {
    return null
  }
  const { course, plans, restaurant } = context
  return (
    <StackableSlideUpModal willBeUnmounted={willBeUnmounted} type="auto">
      <div className={css({ p: '4', spaceY: '5', _pc: { p: '6', pb: '0' } })}>
        <div
          className={css({
            _pc: {
              spaceY: '5',
              // 48px (×ボタン) + 24px (p-6) + 72px (閉じるボタン (16px + 12px * 2 (p-3)) + 16px * 2 (p-4))
              // 少し余裕をもっておく (ボーダーぶんは計算してない)
              minH: '[calc(100vh - 144px)]',
            },
          })}
        >
          <CourseMenu
            courseName={course.name}
            images={course.images}
            imageHeight={155}
            imageWidth={350}
            menuText={course.menuText}
            showReadMoreAccordion={false}
            bg="white"
          />
        </div>
        {!isPC && (
          <div className={css({ spaceY: '3', borderTop: 'light', pb: '10' })}>
            <h3 className={css({ pt: '4', fontWeight: 'bold', fontSize: 'xl' })}>
              予約できるプラン
            </h3>
            <div className={css({ spaceY: '2' })}>
              {plans.map((plan) => (
                <PlanItem
                  key={plan.planId}
                  type="course"
                  plan={plan}
                  course={course}
                  restaurant={restaurant}
                />
              ))}
            </div>
          </div>
        )}
        {isPC && (
          <div
            className={css({
              marginX: '-6',
              position: 'sticky',
              left: '0',
              bottom: '0',
              bg: 'white',
              py: '4',
              px: '6',
              shadow: 'outline.lg',
            })}
          >
            <Button
              color="tertiary"
              spread={true}
              onClick={onClick}
              css={{
                backgroundColor: 'white',
                marginTop: '5',
                display: 'block',
                _pc: { marginTop: '0' },
              }}
            >
              閉じる
            </Button>
          </div>
        )}
      </div>
    </StackableSlideUpModal>
  )
}

const planItemStyles = sva({
  slots: ['root', 'labels', 'bottom', 'description', 'reserve'],
  base: {
    root: {
      display: 'block',
      width: 'full',
      p: '3',
    },
    labels: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '1',
      marginBottom: '1',
      _empty: {
        display: 'none',
      },
    },
    description: {
      display: 'grid',
      transition: '[grid-template-rows 0.3s ease-out]',
      gridTemplateRows: '[0fr]',
      _peerChecked: { gridTemplateRows: '[1fr]', mt: '2' },
    },
    reserve: hstack.raw({ alignItems: 'end', mt: '2' }),
  },
  variants: {
    type: {
      // ガイドのコース一覧表示
      guide: {
        root: {
          _first: {
            borderTop: 'light',
          },
        },
        reserve: {
          justifyContent: 'end',
        },
      },
      // コースモーダルでのプラン表示
      course: {
        root: {
          border: 'medium',
          borderRadius: 'md',
        },
        reserve: {
          justifyContent: 'end',
        },
      },
    },
  },
  defaultVariants: {
    type: 'guide',
  },
})

const nameStyle = cva({
  base: {
    display: 'block',
    fontSize: 'md',
    fontWeight: 'bold',
  },
  variants: {
    link: {
      true: {
        color: 'link',
      },
    },
  },
})

function PlanItem({
  plan,
  restaurant,
  type,
}: PlanItemProps & RecipeVariantProps<typeof planItemStyles>) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setOpen(e.target.checked)
  }, [])

  const classes = planItemStyles({ type })
  return (
    <div
      className={cx(
        classes.root,
        css({
          // peer は一個しか使えないので...
          '&:has(input:checked) .chevron': {
            transform: 'rotate(180deg)',
          },
        })
      )}
    >
      <PlanDetailLink plan={plan} restaurant={restaurant} type={type}>
        <div className={classes.labels}>
          <PlanFreeFlowLabel plan={plan} />
          <PlanRequestReservationLabel plan={plan} />
          <PlanDiscountLabel plan={plan} price={plan} />
        </div>
        <div className={classes.bottom}>
          {/* 一休でもコースモーダルのときはリンクにしない */}
          <p
            className={nameStyle({
              link: import.meta.env.VITE_MODE === 'ikyu' && type === 'guide',
            })}
          >
            {planNameInCourse(plan)}
          </p>
          <PlanSubLabels plan={plan} className={css({ marginTop: '1' })} />
        </div>
      </PlanDetailLink>
      <div className={classes.reserve}>
        {/* PPGの場合は必ずプラン詳細を通るので「詳細を見る」を表示しない */}
        {import.meta.env.VITE_MODE === 'ikyu' && type === 'course' && (
          <label
            htmlFor={id}
            className={hstack({ fontSize: 'sm', color: 'link', gap: '1', flexGrow: 1 })}
          >
            詳細をみる
            <ChevronBottomOutlineIcon className="chevron" />
          </label>
        )}
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            columnGap: '2',
          })}
        >
          <div className={css({ flexShrink: '0', textAlign: 'end' })}>
            <PlanPrice
              listPrice={plan.listPrice}
              bestPrice={plan.bestPrice}
              priceVaried={plan.priceVaried}
              size="xl"
            />
            <PointText price={plan} restaurant={restaurant} />
          </div>
          <ReserveButton plan={plan} restaurant={restaurant} />
        </div>
      </div>
      <input type="checkbox" id={id} className="peer" hidden checked={open} onChange={onChange} />
      {import.meta.env.VITE_MODE === 'ikyu' && type === 'course' && (
        <div className={classes.description}>
          <div className={css({ overflowY: 'hidden' })}>
            <div className={css({ bgColor: 'gray.100', p: '2', rounded: 'sm', spaceY: '3' })}>
              <p className={css({ lineHeight: 'snug', fontSize: 'sm' })}>{plan.description}</p>
              <p className={css({ fontSize: 'xs', color: 'gray.700' })}>
                ※期間や割引率は予告なく変更となる場合があります
                <br />
                ※最大割引適用時の割引率を表示しています
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

type ButtonProps = Pick<PlanItemProps, 'plan' | 'restaurant'> &
  RecipeVariantProps<typeof planItemStyles>

const PlanDetailLink: FC<PropsWithChildren<ButtonProps>> =
  import.meta.env.VITE_MODE === 'ppg' ? PpgPlanDetailLink : IkyuPlanDetailLink

function IkyuPlanDetailLink(props: PropsWithChildren<ButtonProps>) {
  const { onClickDetail } = usePlanItemNavigation({
    planId: props.plan.planId,
    restaurantId: props.restaurant.restaurantId,
  })
  if (props.type === 'course') {
    return props.children
  }
  return (
    <a
      className={css({ color: 'link' })}
      onClick={onClickDetail}
      href={planDetailPagePath(props.restaurant.restaurantId, props.plan.planId)}
    >
      {props.children}
    </a>
  )
}

function PpgPlanDetailLink(props: PropsWithChildren<ButtonProps>) {
  return <div>{props.children}</div>
}

const ReserveButton: FC<ButtonProps> =
  import.meta.env.VITE_MODE === 'ppg' ? PpgReserveButton : IkyuReserveButton

function IkyuReserveButton(props: ButtonProps) {
  const { onClickPlan } = usePlanItemNavigation({
    planId: props.plan.planId,
    restaurantId: props.restaurant.restaurantId,
  })
  return (
    <Button size="sm" color="secondarySolid" onClick={onClickPlan}>
      {RESERVE_LABEL}
    </Button>
  )
}

function PpgReserveButton(props: ButtonProps) {
  const { onClickDetail } = usePlanItemNavigation({
    planId: props.plan.planId,
    restaurantId: props.restaurant.restaurantId,
  })
  return (
    <Button size="sm" color="secondarySolid" asChild>
      <a
        onClick={onClickDetail}
        href={planDetailPagePath(props.restaurant.restaurantId, props.plan.planId)}
      >
        {RESERVE_LABEL}
      </a>
    </Button>
  )
}
